import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  AccessTimeRounded as TimeIcon,
  ArrowBackRounded as BackIcon,
  CalendarMonthRounded as CalendarIcon,
  DirectionsBoatRounded as DepartureIcon,
  FlagRounded as PlannedIcon,
  LoginRounded as ArrivalIcon,
  PlayArrowRounded as StartIcon,
  StopRounded as EndIcon,
} from "@mui/icons-material";
import { updateSelectedTerminalCall } from "../../../data/selectedterminalcalls";

type TimingKey = "planned" | "arrival" | "operationStart" | "operationEnd" | "departure";

type MobileTerminalTimingDialogProps = {
  open: boolean;
  terminalCall: any;
  onClose: () => void;
  onSaved: (terminalCall: any) => void;
};

const timingFields: Array<{ key: TimingKey; label: string; icon: React.ReactNode }> = [
  { key: "planned", label: "Planned", icon: <PlannedIcon /> },
  { key: "arrival", label: "Arrival", icon: <ArrivalIcon /> },
  { key: "operationStart", label: "Operation start", icon: <StartIcon /> },
  { key: "operationEnd", label: "Operation end", icon: <EndIcon /> },
  { key: "departure", label: "Departure", icon: <DepartureIcon /> },
];

const toInputValue = (value?: string | Date | null) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DDTHH:mm") : "";
};

const MobileTerminalTimingDialog: React.FC<MobileTerminalTimingDialogProps> = ({
  open,
  terminalCall,
  onClose,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<TimingKey, string>>({
    planned: "",
    arrival: "",
    operationStart: "",
    operationEnd: "",
    departure: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const timingOrder: TimingKey[] = ["planned", "arrival", "operationStart", "operationEnd", "departure"];

  const getPreviousFieldValue = (key: TimingKey) => {
    const index = timingOrder.indexOf(key);
    if (index <= 0) return null;
    const prevKey = timingOrder[index - 1];
    return values[prevKey] || null;
  };

  const canSetNow = (key: TimingKey) => {
    if (values[key]) return false;
    const prev = getPreviousFieldValue(key);
    if (prev && dayjs(prev).isAfter(dayjs())) return false;
    return true;
  };

  useEffect(() => {
    if (!open || !terminalCall) return;
    setValues({
      planned: toInputValue(terminalCall.planned),
      arrival: toInputValue(terminalCall.arrival),
      operationStart: toInputValue(terminalCall.operationStart),
      operationEnd: toInputValue(terminalCall.operationEnd),
      departure: toInputValue(terminalCall.departure),
    });
    setError("");
  }, [open, terminalCall]);

  const save = async () => {
    try {
      setSaving(true);
      setError("");
      const updated = {
        ...terminalCall,
        ...Object.fromEntries(
          Object.entries(values).map(([key, value]) => [
            key,
            value ? dayjs(value).toISOString() : null,
          ])
        ),
      };

      await dispatch<any>(updateSelectedTerminalCall(updated, false));
      onSaved(updated);
      onClose();
    } catch (exception: any) {
      setError(exception?.message || t("saveFailed", "Saving failed."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullScreen className="mobile-sheet-dialog">
      <AppBar position="sticky" elevation={0} className="mobile-detail-appbar">
        <Toolbar className="mobile-detail-toolbar">
          <IconButton color="inherit" onClick={onClose} disabled={saving} aria-label={t("close", "Close")}>
            <BackIcon />
          </IconButton>
          <Box className="mobile-detail-title">
            <Typography>{t("editTerminalCall", "Edit terminal call")}</Typography>
            <span>{terminalCall?.terminal?.name || "—"}</span>
          </Box>
          <Button color="inherit" onClick={save} disabled={saving} className="mobile-save-button">
            {saving ? <CircularProgress size={18} color="inherit" /> : t("save", "Save")}
          </Button>
        </Toolbar>
      </AppBar>

      <Box className="mobile-timing-dialog-content">
        <Box className="mobile-entity-summary">
          <span className="mobile-entity-icon"><ArrivalIcon /></span>
          <Box>
            <strong>{terminalCall?.terminal?.name || t("terminal", "Terminal")}</strong>
            <span>{terminalCall?.vessel?.name || terminalCall?.vessel?.abbreviation || "—"}</span>
          </Box>
          <span className="mobile-entity-date">
            <CalendarIcon /> {dayjs(terminalCall?.planned || terminalCall?.arrival).format("DD/MM/YYYY")}
          </span>
        </Box>

        <section className="mobile-detail-section">
          <header className="mobile-detail-section-title">
            <TimeIcon />
            <span>{t("timings", "Timings")}</span>
          </header>

          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

          <Box className="mobile-timing-fields">
            {timingFields.map((field) => (
              <Box className="mobile-timing-field" key={field.key}>
                <span className={`mobile-timing-icon mobile-timing-icon--${field.key}`}>{field.icon}</span>
                <TextField
                  label={t(field.key, field.label)}
                  type="datetime-local"
                  value={values[field.key]}
                  disabled={saving}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  onChange={(event) => setValues((current) => ({ ...current, [field.key]: event.target.value }))}
                />
                <Button
                  size="small"
                  variant="text"
                  sx={{ minWidth: 48, whiteSpace: "nowrap", fontSize: "0.55rem", p: 0.25 }}
                  disabled={saving || !canSetNow(field.key)}
                  onClick={() =>
                    setValues((current) => ({
                      ...current,
                      [field.key]: dayjs().format("YYYY-MM-DDTHH:mm"),
                    }))
                  }
                >
                  {t("setToNow", "now")}
                </Button>
              </Box>
            ))}
          </Box>
        </section>

        <Box className="mobile-dialog-actions mobile-dialog-actions--sticky">
          <Button variant="outlined" onClick={onClose} disabled={saving}>{t("cancel", "Cancel")}</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{t("save", "Save")}</Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default MobileTerminalTimingDialog;
